import { Component, computed, inject, ChangeDetectionStrategy } from '@angular/core';
import {
  InfiniteScrollCustomEvent,
  IonButton,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
} from '@ionic/angular/standalone';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  EmptyResultComponent,
  InfiniteScrollHeaderComponent,
  SkeletonCardTableComponent,
  SpinnerLoadingComponent,
  type IInfiniteScrollHeaderConfig,
  type IInfiniteScrollHeaderFilter,
} from '@okcargo-apps/layout-ionic';
import { AccountService } from '../../account';
import { FastpayCardCtaComponent } from '../../fast-payment/fastpay-card-cta/fastpay-card-cta.component';
import { ECapabilities } from '@okcargo-apps/shared-domain-ionic';
import { FreightCarrierStoreService } from '../freight-carrier-store.service';
import { FreightCarrierFinishedCardComponent } from '../freight-carrier-finished-card/freight-carrier-finished-card.component';

@Component({
  selector: 'lib-freight-carrier-finished-list',
  templateUrl: './freight-carrier-finished-list.component.html',
  styleUrls: ['./freight-carrier-finished-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    IonInfiniteScrollContent,
    IonInfiniteScroll,
    InfiniteScrollHeaderComponent,
    IonButton,
    TranslateModule,
    FreightCarrierFinishedCardComponent,
    SkeletonCardTableComponent,
    SpinnerLoadingComponent,
    FastpayCardCtaComponent,
    EmptyResultComponent,
  ],
})
export class FreightCarrierFinishedListComponent {
  private readonly freightCarrierStoreService = inject(FreightCarrierStoreService);
  private readonly accountService = inject(AccountService);
  private readonly translateService = inject(TranslateService);

  protected hasViewPricesCapability = computed(() =>
    this.accountService.account().capabilities.includes(ECapabilities.VIEW_PRICES),
  );

  protected readonly vm = this.freightCarrierStoreService.finishedList;

  protected readonly headerConfig = computed<IInfiniteScrollHeaderConfig>(() => {
    const currentVm = this.vm();

    return {
      text: this.translateService.instant('freight.list.item_count', { count: currentVm.totalElements }),
      lastUpdated: currentVm.lastLoadedAt,
      staleMessage: currentVm.isStale ? this.translateService.instant('general.stale_data') : undefined,
      refreshButton: currentVm.isStale
        ? {
            label: this.translateService.instant('general.refresh'),
            action: () => this.onRefreshUser(),
          }
        : undefined,
      appliedFilters: this.appliedFiltersChips(),
    };
  });

  private readonly appliedFiltersChips = computed<IInfiniteScrollHeaderFilter[]>(() => {
    const filters = this.freightCarrierStoreService.activeFinishedFilters();
    const chips: IInfiniteScrollHeaderFilter[] = [];

    if (filters?.withoutDigitizedDeliveryNotes) {
      chips.push({
        label: this.translateService.instant('freight.search.without_digitized_delivery_notes'),
        clear: () => this.freightCarrierStoreService.clearFinishedFilter('withoutDigitizedDeliveryNotes'),
      });
    }

    if (filters?.pendingPaperDeliveryNote) {
      chips.push({
        label: this.translateService.instant('freight.search.paper_note_pending'),
        clear: () => this.freightCarrierStoreService.clearFinishedFilter('pendingPaperDeliveryNote'),
      });
    }

    return chips;
  });

  protected onIonInfinite(event: InfiniteScrollCustomEvent): void {
    this.freightCarrierStoreService.loadNextFinishedPage();
    event.target.complete();
  }

  protected onRetry(): void {
    this.freightCarrierStoreService.retryFinished();
  }

  protected onRefreshUser(): void {
    this.freightCarrierStoreService.reloadFinishedList('user');
  }
}
